import type { DeviceType, DiagramNode, PortLayout } from './types.js';
import type { DevicePort, PortSide } from './devicePorts.js';

/**
 * Equipment port roles (WAN copper, LAN, generic Eth, SFP).
 * Combo uplink: choosing SFP as uplink removes WAN RJ45 (typical diagram convention).
 */
export type PortRole = 'wan' | 'lan' | 'eth' | 'sfp';

export type PortProfile = {
  id: string;
  labelUk: string;
  deviceTypes: readonly DeviceType[];
  /** wan = copper WAN; sfp = fiber uplink (no WAN RJ45); none = switch/endpoint */
  uplink: 'wan' | 'sfp' | 'none';
  wan: number;
  lan: number;
  eth: number;
  sfp: number;
};

export const PORT_PROFILES: readonly PortProfile[] = [
  {
    id: 'router_1w4l',
    labelUk: 'Роутер / ТД: 1 WAN + 4 LAN',
    deviceTypes: ['router', 'wifi_router', 'firewall', 'modem'],
    uplink: 'wan',
    wan: 1,
    lan: 4,
    eth: 0,
    sfp: 0,
  },
  {
    id: 'router_1w3l',
    labelUk: 'Роутер (hAP lite): 1 WAN + 3 LAN',
    deviceTypes: ['router', 'wifi_router'],
    uplink: 'wan',
    wan: 1,
    lan: 3,
    eth: 0,
    sfp: 0,
  },
  {
    id: 'router_1w4l_1sfp',
    labelUk: 'Роутер: 1 WAN + 4 LAN + 1 SFP',
    deviceTypes: ['router', 'wifi_router', 'firewall'],
    uplink: 'wan',
    wan: 1,
    lan: 4,
    eth: 0,
    sfp: 1,
  },
  {
    id: 'router_sfp_uplink_5lan',
    labelUk: 'Роутер: uplink SFP + 5 LAN (без WAN)',
    deviceTypes: ['router', 'wifi_router', 'firewall'],
    uplink: 'sfp',
    wan: 0,
    lan: 5,
    eth: 0,
    sfp: 1,
  },
  {
    id: 'router_rb2011',
    labelUk: 'Роутер RB2011-like: 1 WAN + 9 LAN + 1 SFP',
    deviceTypes: ['router', 'wifi_router'],
    uplink: 'wan',
    wan: 1,
    lan: 9,
    eth: 0,
    sfp: 1,
  },
  {
    id: 'firewall_2w2l',
    labelUk: 'Firewall: 2 WAN + 2 LAN',
    deviceTypes: ['firewall'],
    uplink: 'wan',
    wan: 2,
    lan: 2,
    eth: 0,
    sfp: 0,
  },
  {
    id: 'switch_5',
    labelUk: 'Комутатор: 5 Eth',
    deviceTypes: ['switch'],
    uplink: 'none',
    wan: 0,
    lan: 0,
    eth: 5,
    sfp: 0,
  },
  {
    id: 'switch_8',
    labelUk: 'Комутатор: 8 Eth',
    deviceTypes: ['switch'],
    uplink: 'none',
    wan: 0,
    lan: 0,
    eth: 8,
    sfp: 0,
  },
  {
    id: 'switch_8_2sfp',
    labelUk: 'Комутатор: 8 Eth + 2 SFP',
    deviceTypes: ['switch'],
    uplink: 'none',
    wan: 0,
    lan: 0,
    eth: 8,
    sfp: 2,
  },
  {
    id: 'switch_16_2sfp',
    labelUk: 'Комутатор: 16 Eth + 2 SFP',
    deviceTypes: ['switch'],
    uplink: 'none',
    wan: 0,
    lan: 0,
    eth: 16,
    sfp: 2,
  },
  {
    id: 'switch_24_2sfp',
    labelUk: 'Комутатор: 24 Eth + 2 SFP',
    deviceTypes: ['switch'],
    uplink: 'none',
    wan: 0,
    lan: 0,
    eth: 24,
    sfp: 2,
  },
  {
    id: 'switch_24_4sfp',
    labelUk: 'Комутатор: 24 Eth + 4 SFP',
    deviceTypes: ['switch'],
    uplink: 'none',
    wan: 0,
    lan: 0,
    eth: 24,
    sfp: 4,
  },
  {
    id: 'switch_48_4sfp',
    labelUk: 'Комутатор: 48 Eth + 4 SFP',
    deviceTypes: ['switch'],
    uplink: 'none',
    wan: 0,
    lan: 0,
    eth: 48,
    sfp: 4,
  },
  {
    id: 'ap_1eth',
    labelUk: 'Точка доступу: 1 PoE',
    deviceTypes: ['access_point'],
    uplink: 'none',
    wan: 0,
    lan: 0,
    eth: 1,
    sfp: 0,
  },
  {
    id: 'ap_2eth',
    labelUk: 'Точка доступу: 2 Eth',
    deviceTypes: ['access_point'],
    uplink: 'none',
    wan: 0,
    lan: 0,
    eth: 2,
    sfp: 0,
  },
  {
    id: 'lte_1eth',
    labelUk: 'LTE антена / CPE: 1 Eth (PoE)',
    deviceTypes: ['lte_antenna'],
    uplink: 'none',
    wan: 0,
    lan: 0,
    eth: 1,
    sfp: 0,
  },
  {
    id: 'endpoint_1',
    labelUk: 'Кінцевий: 1 Eth',
    deviceTypes: [
      'desktop',
      'laptop',
      'ip_phone',
      'printer',
      'camera',
      'display',
      'controller',
      'iot',
      'tablet',
      'lte_antenna',
      'satellite',
      'ups',
      'power_station',
      'generic',
      'server',
      'nas',
    ],
    uplink: 'none',
    wan: 0,
    lan: 0,
    eth: 1,
    sfp: 0,
  },
  {
    id: 'endpoint_2',
    labelUk: 'Кінцевий: 2 Eth',
    deviceTypes: [
      'desktop',
      'laptop',
      'ip_phone',
      'server',
      'nas',
      'controller',
      'generic',
      'ups',
      'power_station',
      'lte_antenna',
      'satellite',
    ],
    uplink: 'none',
    wan: 0,
    lan: 0,
    eth: 2,
    sfp: 0,
  },
  {
    id: 'server_2eth_1sfp',
    labelUk: 'Сервер / NAS: 2 Eth + 1 SFP',
    deviceTypes: ['server', 'nas'],
    uplink: 'none',
    wan: 0,
    lan: 0,
    eth: 2,
    sfp: 1,
  },
] as const;

const PROFILE_BY_ID = new Map(PORT_PROFILES.map((p) => [p.id, p]));

export function getPortProfile(id?: string | null): PortProfile | undefined {
  if (!id) return undefined;
  return PROFILE_BY_ID.get(id);
}

export function portProfilesForType(type: DeviceType): PortProfile[] {
  return PORT_PROFILES.filter((p) => p.deviceTypes.includes(type));
}

export function defaultPortProfileId(type: DeviceType): string | undefined {
  switch (type) {
    case 'router':
    case 'wifi_router':
      return 'router_1w4l';
    case 'access_point':
      return 'ap_1eth';
    case 'lte_antenna':
      return 'lte_1eth';
    case 'firewall':
      return 'firewall_2w2l';
    case 'modem':
      return 'router_1w4l';
    case 'switch':
      return 'switch_8';
    case 'server':
    case 'nas':
      return 'endpoint_2';
    case 'ip_phone':
    case 'desktop':
    case 'laptop':
    case 'printer':
    case 'camera':
    case 'display':
    case 'controller':
    case 'satellite':
    case 'ups':
    case 'power_station':
    case 'generic':
      return 'endpoint_1';
    default:
      return undefined;
  }
}

export function resolvePortProfile(
  node: Pick<DiagramNode, 'type' | 'portProfileId'>,
): PortProfile | undefined {
  const explicit = getPortProfile(node.portProfileId);
  if (explicit && explicit.deviceTypes.includes(node.type)) return explicit;
  // Only auto-apply when user picked a profile id or we want defaults for gateway gear
  if (node.portProfileId) return getPortProfile(defaultPortProfileId(node.type));
  return getPortProfile(defaultPortProfileId(node.type));
}

export function profileWiredCount(profile: PortProfile): number {
  const wan = profile.uplink === 'sfp' ? 0 : profile.wan;
  return wan + profile.lan + profile.eth + profile.sfp;
}

function tAlongSide(side: PortSide, idxOnSide: number, totalOnSide: number): number {
  if (totalOnSide <= 1) return 0.5;
  const sequential = idxOnSide / (totalOnSide + 1);
  if (side === 'right' || side === 'top') return 1 - sequential;
  return sequential;
}

function assignCopperSides(count: number, layout: PortLayout): PortSide[] {
  if (count <= 0) return [];
  if (layout === 'left') return Array.from({ length: count }, () => 'left' as const);
  if (layout === 'right') return Array.from({ length: count }, () => 'right' as const);
  if (layout === 'both') {
    const left = Math.ceil(count / 2);
    return [
      ...Array.from({ length: left }, () => 'left' as const),
      ...Array.from({ length: count - left }, () => 'right' as const),
    ];
  }
  const order: PortSide[] = ['left', 'bottom', 'right', 'top'];
  const base = Math.floor(count / 4);
  const rem = count % 4;
  const out: PortSide[] = [];
  for (let i = 0; i < 4; i++) {
    const n = base + (i < rem ? 1 : 0);
    for (let j = 0; j < n; j++) out.push(order[i]!);
  }
  return out;
}

type RoleSpec = { role: PortRole; id: string; label: string };

function roleSpecs(profile: PortProfile): RoleSpec[] {
  const out: RoleSpec[] = [];
  const wan = profile.uplink === 'sfp' ? 0 : profile.wan;
  for (let i = 1; i <= wan; i++) {
    out.push({
      role: 'wan',
      id: wan === 1 ? 'WAN' : `WAN-${i}`,
      label: wan === 1 ? 'WAN' : `WAN-${i}`,
    });
  }
  for (let i = 1; i <= profile.lan; i++) {
    out.push({ role: 'lan', id: `LAN-${i}`, label: `LAN-${i}` });
  }
  for (let i = 1; i <= profile.eth; i++) {
    out.push({ role: 'eth', id: `Eth-${i}`, label: `Eth-${i}` });
  }
  for (let i = 1; i <= profile.sfp; i++) {
    const isUplink = profile.uplink === 'sfp' && i === 1;
    out.push({
      role: 'sfp',
      id: `SFP-${i}`,
      label: isUplink ? 'SFP↑' : profile.sfp === 1 ? 'SFP' : `SFP-${i}`,
    });
  }
  return out;
}

export function buildPortsFromProfile(
  profile: PortProfile,
  layout: PortLayout = 'both',
): DevicePort[] {
  const specs = roleSpecs(profile);
  if (!specs.length) return [];

  const copperSpecs = specs.filter((s) => s.role !== 'sfp');
  const copperSides = assignCopperSides(copperSpecs.length, layout);
  let copperIdx = 0;

  const sides: PortSide[] = specs.map((s) => {
    if (s.role === 'sfp') return 'top';
    if (s.role === 'wan') return layout === 'right' ? 'right' : 'left';
    return copperSides[copperIdx++] ?? 'left';
  });

  const perSide = { left: 0, right: 0, top: 0, bottom: 0 };
  const sideTotals = { left: 0, right: 0, top: 0, bottom: 0 };
  for (const s of sides) sideTotals[s] += 1;

  return specs.map((spec, i) => {
    const side = sides[i]!;
    perSide[side] += 1;
    return {
      id: spec.id,
      label: spec.label,
      index: i + 1,
      side,
      t: tAlongSide(side, perSide[side], sideTotals[side]),
      kind: 'ethernet' as const,
      role: spec.role,
    };
  });
}
